import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import moment from 'moment'
import { ToolTip, Div, H2 } from '@driftt/tide-core';
import { Table } from '@driftt/tide-table'
import { CHART_NO_DATA_TEXT, signatureClickColumns, clickColumns, clickColumnsOutboundSequence } from 'constants/ReportConstants'
import GraphContainer from 'components/Reports/EmailPerformance/Graphs'
import classnames from 'classnames'
import RowFormatter from 'components/Reports/EmailPerformance/TableRowFormatter'
import { EmptyGraphAndPage } from 'components/Reports/EmailPerformance/EmptyState'
import { roundPercentValue } from 'components/Reports/EmailPerformance/utils'
import { get } from 'lodash'


class TotalClicks extends Component {
  constructor(props) {
    super(props)

    this.state = {
      activeSection: 'CLICKED',
      currentData: null
    }
  }

  getClassNames(activeSection) {
    const defaultClass = 'reports-data-callout-item'
    return {
      sends: classnames(defaultClass, {
        'reports-data-callout-item--highlighted': activeSection === 'SENT'
      }),
      opens: classnames(defaultClass, {
        'reports-data-callout-item--highlighted': activeSection === 'OPENED'
      }),
      delivery: classnames(defaultClass, {
        'reports-data-callout-item--highlighted': activeSection === 'DELIVERED'
      }),
      clicks: classnames(defaultClass, {
        'reports-data-callout-item--highlighted': activeSection === 'CLICKED'
      }),
      conversations: classnames(defaultClass, {
        'reports-data-callout-item--highlighted': activeSection === 'CONVERSATION_INFLUENCED'
      }),
    }
  }

  componentDidMount() {
    this.setState({
      currentData: this.generateGraphData()
    })
  }

  changeGraphData(section) {
    this.setState({
      activeSection: section,
      currentData: this.generateGraphData(section)
    })
  }

  generateGraphData(section) {
    const { activeSection } = this.state
    const { data } = this.props
    const subField = section || activeSection
    const currentGraphData = []
    const previousGraphData = []

    const rawCurrentDates = data.byDate.current
    const rawPreviousDates = data.byDate.previous

    rawCurrentDates.forEach((date, i) => {
      const obj = [
        moment.utc(date.date).valueOf(),
        date.metrics[subField].value
      ]
      currentGraphData.push(obj)
    })

    rawPreviousDates.forEach((date, i) => {
      if (currentGraphData[i] === undefined)
        return

      let y = 0
      if (date.metrics.hasOwnProperty(subField)) {
        y = date.metrics[subField].value
      }

      const obj = [
        currentGraphData[i][0],
        y
      ]
      previousGraphData.push(obj)
    })

    return {
      current: currentGraphData,
      previous: previousGraphData
    }
  }

  generateTableRow = (email, index) => {
    const { data, playbook } = this.props
    const emailCampaignReport =  data.byEmail.find((mail) => mail.campaignId === email.campaignId)
    return (
      playbook.reportType === 'OUTBOUND_SEQUENCE' ?
        RowFormatter.ClickRowsOutboundSequence(emailCampaignReport, email.title, index, playbook.reportType)
        : RowFormatter.ClickRows(emailCampaignReport, email.title, index)
    )
  }

  generateSignatureRows = (userData) => {
    const { data, orgUsers } = this.props

    // change [{ id: 1, name: 'jacob', ...}, { id: 2, name: 'foster', ...}]
    // into:
    // { 1: { id: 1, name: 'jacob', ...}, 2: { id: 2, name: 'foster', ... }}
    const userIdToUserMap = {}
    orgUsers.forEach(user => userIdToUserMap[user.id] = user)

    return !!data ? data.byUser
      .map(userData => ({ user: userIdToUserMap[userData.userId], metrics: userData.metrics}))
      .filter(userData => !!userData.user)
      .map(RowFormatter.SignatureClickRows) : []
  }

  getBaseValue() {
    const { data, playbook, playbookReportType } = this.props
    const isOutboundPlaybook = playbook.reportType === 'OUTBOUND_SEQUENCE'
    const isSignaturePlaybook = playbookReportType === 'signature'
    const total = data.total

    return (
      isOutboundPlaybook ?
        total.SENT.value  // SENT is base for outbound playbooks
        :
        isSignaturePlaybook ?
          total.OPENED.value  // OPENED is base for signature playbooks
          :
          total.DELIVERED.value  // DELIVERED is base for email playbooks
    )
  }

  renderTable() {
    const { data, playbook, playbookReportType, } = this.props
    const { actions } = playbook
    const { action } = actions[0]
    const emailCampaigns = action.emails ? action.emails : [{...action}]
    const isOutboundPlaybook = playbook.reportType === 'OUTBOUND_SEQUENCE'
    const isSignaturePlaybook = playbookReportType === 'signature'

    const rows = !data ?
      []
      :
      isSignaturePlaybook ?
        this.generateSignatureRows()
        :
        emailCampaigns.map(this.generateTableRow)

    const columns = isSignaturePlaybook ?
      signatureClickColumns
      :
      isOutboundPlaybook ?
        clickColumnsOutboundSequence
        :
        clickColumns

    return <Table columns={columns} className="email-reports-table" data={rows} />
  }

  renderCalloutHeader(className, value, changeToGraph, title, tooltipComponent, conversionRate) {
    return (
      <ToolTip component={tooltipComponent} flow="up">
        <Div className={className} onClick={() => this.changeGraphData(changeToGraph)}>
          <Div className="callout-header">{title}</Div>
          <Div className="callout-large">{value}</Div>
          <Div className="callout-subtext">{conversionRate}</Div>
        </Div>
      </ToolTip>
    )
  }

  render() {
    const { activeState, playbook, data, playbookReportType } = this.props
    const { activeSection, currentData } = this.state
    const { sends, delivery, opens, clicks, conversations } = this.getClassNames(activeSection)

    const isOutboundPlaybook = playbook.reportType === 'OUTBOUND_SEQUENCE'
    const isSignaturePlaybook = playbookReportType === 'signature'
    const total = data.total
    const baseValue = this.getBaseValue()

    const deliveredValue = get(total, 'DELIVERED.value', 0)
    const sentValue = get(total, 'SENT.value', 0)
    const openedValue = get(total, 'OPENED.value', 0)
    const clickedValue = get(total, 'CLICKED.value', 0)
    const conversationValue = get(total, 'CONVERSATION_INFLUENCED.value', 0)

    const deliveredPercent = roundPercentValue(deliveredValue, sentValue)
    const openPercent = roundPercentValue(openedValue, baseValue)
    const clickPercent = roundPercentValue(clickedValue, baseValue)
    const conversationPercent = roundPercentValue(conversationValue, baseValue)

    // TODO: may have to revisit this
    const hasDisplayData = (
      (deliveredValue !== 0 && deliveredValue !== undefined) ||
      (sentValue !== 0 && sentValue !== undefined) ||
      (openedValue !== 0 && openedValue !== undefined) ||
      (clickedValue !== 0 && clickedValue !== undefined) ||
      (conversationValue !== 0 && conversationValue !== undefined)
    )

    return (
      <Div className="email-performance-breakdown email-performance-breakdown--clicks">
        {hasDisplayData ?
          <Div className="reports-data-callout">
            {!isSignaturePlaybook && this.renderCalloutHeader(
              sends,
              sentValue,
              'SENT',
              'Sent',
              <Div className="metric-tooltip-center">
                Total amount of sent emails<br /> in this playbook.
              </Div>,
            )}
            {!isOutboundPlaybook && !isSignaturePlaybook &&
              this.renderCalloutHeader(
                delivery,
                deliveredValue,
                'DELIVERED',
                'Delivered',
                <Div className="metric-tooltip-center">
                  Total amount of delivered<br />emails in this playbook.
                </Div>,
                `(${deliveredPercent}%)`,
              )
            }
            {this.renderCalloutHeader(
              opens,
              openedValue,
              'OPENED',
              isSignaturePlaybook ? 'Views' : 'Opens',
              <Div className="metric-tooltip-center">
                Total amount of unique<br />{isSignaturePlaybook ? 'views' : 'opens'} in this playbook.
              </Div>,
              !isSignaturePlaybook ? `(${openPercent}%)` : undefined,
            )}
            {this.renderCalloutHeader(
              clicks,
              clickedValue,
              'CLICKED',
              'Clicks',
              <Div className="metric-tooltip-center">
                Total unique clicks<br />in this playbook.
              </Div>,
              `(${clickPercent}%)`,
            )}
            {isSignaturePlaybook &&
              this.renderCalloutHeader(
                conversations,
                conversationValue,
                'CONVERSATION_INFLUENCED',
                'Conversations',
                <Div className="metric-tooltip-center">
                  Total conversations that<br />were caused by this signature.
                </Div>,
                `(${conversationPercent}%)`,
              )
            }
          </Div> :
          <Fragment>
            <Div className="reports-graph-data-overlay">
              <EmptyGraphAndPage />
              <H2>{CHART_NO_DATA_TEXT}</H2>
            </Div>
          </Fragment>
        }
        <Div className="reports-graph-container">
          {!!currentData &&
            <GraphContainer activeTab={activeState} graphData={currentData}/>
          }
        </Div>
        <Div className="reports-table-container">
          {this.renderTable()}
        </Div>
      </Div>
    )
  }
}

const mapStateToProps = state => ({
  orgUsers: state.org.users,
})

export default connect(mapStateToProps)(TotalClicks)
